<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core'
import { useUpVoteMutation } from '~~/src/composables/mutations/upvote/useUpvoteMutation'
import type { List } from '~~/src/types/list'

interface IProps {
  list: List
  variant?: 'default' | 'mini'
}

const props = withDefaults(
  defineProps<IProps>(),
  {
    variant: 'default',
  },
)
const { list, variant } = toRefs(props)
const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('md')

const buttonRef = ref<HTMLDivElement>()

const votesCount = computed({
  get: () => list.value.nVotes,
  set: (count) => {
    list.value.nVotes = count
  },
})

const iconSize = computed(() => variant.value === 'mini' ? (isMobile.value ? '20px' : '18px') : '32px')

const { mutate, isPending: isVoting } = useUpVoteMutation()

const upvote = async () => {
  if (isVoting.value)
    return

  mutate(list)
}

const upvoteList = useUpdateListVote()
watch(upvoteList, () => {
  if (upvoteList.value?.id === list.value.id) {
    if (upvoteList.value.nVotes || upvoteList.value.nVotes === 0)
      votesCount.value = upvoteList.value.nVotes
    list.value.isVoted = upvoteList.value.state === 'added'
  }
})
</script>

<script lang="ts">
export default defineComponent({
  name: 'ListUpvoteButton',
})
</script>

<template>
  <button
    ref="buttonRef"
    flex items-center gap-1
    :pr="variant === 'mini' ? '4px' : '8px'"
    :pl="isMobile ? '10px' : (variant === 'mini' ? '10px' : '12px')"
    :h="isMobile ? (variant === 'mini' ? '34px' : '40px') : (variant === 'mini' ? '32px' : '48px')"
    :w="variant === 'default' ? 'full' : 'auto'"
    class="relative group/upvote bg-white/80
        backdrop-blur-[8px]
        rounded-full cursor-pointer duration-125 transition-colors center
        border
      "
    :class="{
      'color-soft-black hover:color-primary border-soft-gray hover:border-primary': !list.isVoted,
      'color-primary border-primary': list.isVoted,
    }"
    @click.prevent.stop="upvote()"
  >
    <Text
      v-if="variant === 'mini'"
      variant="caption"
      color="inherit"
      :style="{ fontSize: '12px' }"
    >
      <slot>
        <div flex gap-2 justify-center>
          <span>
            {{ formatThousands(votesCount) }}
          </span>
        </div>
      </slot>
    </Text>
    <Heading
      v-else
      :level="isMobile ? '5' : '4'"
      color="inherit"
      :flex-grow="variant === 'default' ? '1' : '0'"
    >
      <slot>
        <div flex gap-2 justify-center whitespace-nowrap>
          <span v-if="variant === 'default'">
            {{ list.isVoted ? 'Upvoted' : 'Upvote' }}
          </span>
          <span>
            {{ formatThousands(votesCount) }}
          </span>
        </div>
      </slot>
    </Heading>

    <div>
      <Transition name="fade" mode="out-in">
        <IconLogo v-if="list.isVoted" :width="iconSize" :height="iconSize" />

        <IconLogoInactive v-else :width="iconSize" :height="iconSize" />
      </Transition>
    </div>
  </button>
</template>
